import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  getDocs,
  serverTimestamp,
  doc,
  getDoc,
  orderBy,
} from 'firebase/firestore';
import { FaBriefcase, FaCalendarAlt, FaChild, FaVenusMars, FaHome, FaSchool, FaBuilding, FaPlus } from 'react-icons/fa';
import Spinner from './Spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const REQUIREMENT_TYPES = [
  { id: 'shadow_teacher', name: 'Shadow Teacher' },
  { id: 'occupational_therapist', name: 'Occupational Therapist' },
  { id: 'behavioral_therapist', name: 'Behavioral Therapist' },
  { id: 'speech_therapist', name: 'Speech Therapist' },
  { id: 'special_educator', name: 'Special Educator' },
  { id: 'doctor', name: 'Doctor Appointment' },
];

const GENDERS = [
  { id: 'male', name: 'Male' },
  { id: 'female', name: 'Female' },
  { id: 'other', name: 'Other' },
];

const WORK_LOCATIONS = [
  { id: 'home', name: 'At Home', icon: <FaHome className="mr-2" /> },
  { id: 'cdc', name: 'CDC Center', icon: <FaBuilding className="mr-2" /> },
  { id: 'school', name: 'School', icon: <FaSchool className="mr-2" /> },
];

const LOCATIONS = [
  'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai',
  'Kolkata', 'Pune', 'Ahmedabad', 'Jaipur', 'Chandigarh'
];

export default function ParentDashboardContent({ user, profile, role }) {
  // Use the user from props or fallback to auth
  const auth = getAuth();
  const currentUser = user || auth.currentUser;
  // --- State for New Requirement Form ---
  const [form, setForm] = useState({
    type: '',
    date: '',
    age: '',
    gender: '',
    location: '',
    target: '',
    description: '',
    skills: [],
    experience: '',
    schedule: 'Part-time',
    salary: ''
  });
  
  const [skillsInput, setSkillsInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isFormOpen, setIsFormOpen] = useState(false);
  
  // --- State for Previous Requests ---
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);

  // Search functionality has been removed as per user request

  // --- Fetch previous requests ---
  useEffect(() => {
    if (!currentUser) {
      console.log('No current user, skipping requests fetch');
      return;
    }
    
    console.log('Fetching requests for user:', currentUser.uid);
    setLoadingRequests(true);
    
    const q = query(
      collection(db, "jobPosts"), 
      where("posterId", "==", currentUser.uid),
      where("posterType", "==", "parent"),
      orderBy("postedAt", "desc")
    );
    
    console.log('Firestore query created:', q);
    
    const unsub = onSnapshot(
      q, 
      (snap) => {
        const requestsData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Received requests data:', requestsData);
        setRequests(requestsData);
        setLoadingRequests(false);
      }, 
      (error) => {
        console.error("Error fetching requirements:", error);
        setLoadingRequests(false);
      }
    );
    
    return () => {
      console.log('Cleaning up requests listener');
      unsub();
    };
  }, [currentUser]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle skills input
  const handleSkillsChange = (e) => {
    const value = e.target.value;
    setSkillsInput(value);
    
    // Update skills array
    const skillsArray = value
      .split(',')
      .map(skill => skill.trim())
      .filter(skill => skill.length > 0);
    
    setForm(prev => ({ ...prev, skills: skillsArray }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!form.type) {
      errors.type = 'Please select a requirement type';
      isValid = false;
    }
    if (!form.age) {
      errors.age = 'Please enter child\'s age';
      isValid = false;
    }
    if (!form.gender) {
      errors.gender = 'Please select child\'s gender';
      isValid = false;
    }
    if (!form.location) {
      errors.location = 'Please select a location';
      isValid = false;
    }
    if (!form.target) {
      errors.target = 'Please select a work location';
      isValid = false;
    }
    if (!form.date) {
      errors.date = 'Please select a preferred date';
      isValid = false;
    }
    if (!form.description) {
      errors.description = 'Please provide some details about your requirement';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Toggle form visibility and reset errors when closing
  const toggleForm = () => {
    setIsFormOpen(!isFormOpen);
    // Reset form errors when closing
    if (isFormOpen) {
      setFormErrors({});
    }
  };

  // --- Handle New Requirement Submit ---
  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Get user profile for contact info
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data();
      
      const selectedType = REQUIREMENT_TYPES.find(t => t.id === form.type)?.name || form.type;
      const selectedGender = GENDERS.find(g => g.id === form.gender)?.name || form.gender;
      const selectedLocation = WORK_LOCATIONS.find(l => l.id === form.target)?.name || form.target;
      
      // Prepare job post data with posterType 'parent' for filtering
      const jobPostData = {
        // Basic job details
        title: selectedType,
        description: form.description || `Looking for a ${selectedType} for my child (${form.age} years, ${selectedGender})`,
        type: form.schedule || 'Part-time',
        workLocation: [selectedLocation],
        role: selectedType,
        skills: form.skills,
        experience: form.experience,
        salary: form.salary,
        
        // Location
        location: {
          city: form.location,
          area: form.location
        },
        
        // Status and metadata
        status: 'open',
        posterType: 'parent',
        
        // Poster information
        posterId: currentUser.uid,
        posterEmail: currentUser.email,
        posterName: currentUser.displayName || currentUser.email.split('@')[0],
        
        // Child information (specific to parent requirements)
        childInfo: {
          age: form.age,
          gender: selectedGender,
          preferredDate: form.date
        },
        
        // Timestamps
        postedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Search optimization
        searchTerms: [
          selectedType.toLowerCase(),
          form.location.toLowerCase(),
          selectedLocation.toLowerCase(),
          'parent',
          form.schedule ? form.schedule.toLowerCase() : 'part-time',
          'child',
          selectedGender.toLowerCase(),
          'requirement',
          'therapy',
          'special needs',
          'autism',
          ...form.skills.map(skill => skill.toLowerCase())
        ].filter(Boolean)
      };
      
      // Save to the common jobPosts collection
      await addDoc(collection(db, 'jobPosts'), jobPostData);
      
      // Reset form
      setForm({
        type: '',
        date: '',
        age: '',
        gender: '',
        location: '',
        target: '',
        description: '',
        skills: [],
        experience: '',
        schedule: 'Part-time',
        salary: ''
      });
      setSkillsInput('');
      setIsFormOpen(false);
      
      // Show success toast
      toast.success('Requirement posted successfully!', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (err) {
      console.error('Error posting requirement:', err);
      toast.error('Error posting requirement. Please try again.', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setSubmitting(false);
    }
  }

  // Search functionality has been removed as per user request

  // --- Express Interest ---
  async function handleExpressInterest(targetId, role) {
    const auth = getAuth();
    const parentId = auth.currentUser.uid;
    const interestRef = doc(collection(db, "interests"));
    await setDoc(interestRef, {
      parentId,
      targetId,
      role,
      timestamp: serverTimestamp(),
    });
    setToast("Interest expressed!");
    setTimeout(() => setToast(""), 2000);
  }

  return (
    <div className="max-w-5xl mx-auto mt-10 pb-20">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Toast */}
      {toast && <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-green-600 text-white px-6 py-2 rounded shadow-lg z-50">{toast}</div>}

      {/* 2. Post New Requirement */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-10 border border-indigo-100">
        <div className="flex justify-between items-center cursor-pointer" onClick={toggleForm}>
          <h2 className="text-2xl font-bold text-indigo-700 flex items-center">
            <FaBriefcase className="mr-2" />
            {isFormOpen ? 'Post a New Requirement' : 'Have a Specific Need?'}
          </h2>
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={(e) => {
              e.stopPropagation();
              toggleForm();
            }}
          >
            {isFormOpen ? (
              <span className="text-lg font-bold">−</span>
            ) : (
              <span className="text-lg font-bold">+</span>
            )}
          </button>
        </div>

        {isFormOpen && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                {renderField('Requirement Type', 'type', 'select', REQUIREMENT_TYPES)}
                
                {renderField('Child\'s Age', 'age', 'number', null, 'Enter child\'s age')}
                
                {renderField('Child\'s Gender', 'gender', 'select', GENDERS)}
                
                <div className="grid grid-cols-2 gap-4">
                  {renderField('Preferred Date', 'date', 'date')}
                  {renderField('Schedule', 'schedule', 'select', [
                    { id: 'part-time', name: 'Part-time' },
                    { id: 'full-time', name: 'Full-time' },
                    { id: 'flexible', name: 'Flexible' }
                  ])}
                </div>
              </div>
              
              {/* Right Column */}
              <div className="space-y-4">
                {renderField('Location', 'location', 'text', null, 'Enter your city')}
                
                {renderField('Work Location', 'target', 'select', WORK_LOCATIONS.map(loc => ({
                  id: loc.id,
                  name: `${loc.icon} ${loc.name}`
                })))}
                
                <div className="grid grid-cols-2 gap-4">
                  {renderField('Experience', 'experience', 'select', [
                    { id: '0-1', name: '0-1 years' },
                    { id: '1-3', name: '1-3 years' },
                    { id: '3-5', name: '3-5 years' },
                    { id: '5+', name: '5+ years' }
                  ])}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Salary (Optional)
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">₹</span>
                      </div>
                      <input
                        type="number"
                        name="salary"
                        value={form.salary}
                        onChange={handleChange}
                        placeholder="e.g. 15000"
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md p-3 border"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">/month</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Required Skills (Optional)
                  </label>
                  <input
                    type="text"
                    value={skillsInput}
                    onChange={handleSkillsChange}
                    placeholder="e.g. ABA, Speech Therapy, Special Education"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.skills.length > 0 ? (
                      form.skills.map((skill, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {skill}
                          <button
                            type="button"
                            onClick={() => {
                              const newSkills = [...form.skills];
                              newSkills.splice(index, 1);
                              setForm({ ...form, skills: newSkills });
                              setSkillsInput(newSkills.join(', '));
                            }}
                            className="ml-1.5 inline-flex items-center justify-center text-blue-400 hover:text-blue-600"
                          >
                            <span className="sr-only">Remove skill</span>
                            <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500">Type and press enter to add skills</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Details <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={4}
                placeholder="Please provide details about your requirement, preferred timings, specific needs, etc."
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  formErrors.description ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {formErrors.description && (
                <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
              )}
            </div>
            
            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={toggleForm}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Posting...
                  </>
                ) : (
                  <>
                    <FaBriefcase className="mr-2" />
                    Post Requirement
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* 3. Your Previous Requests */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-10 border border-indigo-100">
        <h2 className="text-2xl font-bold text-indigo-700 mb-6">Your Previous Requests</h2>
        
        {loadingRequests ? (
          <div className="flex justify-center my-8">
            <Spinner />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FaClipboardList className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-lg font-medium">No requests yet</p>
            <p className="text-gray-500 mt-1">Your posted requirements will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <div key={request.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{request.title}</h3>
                    <p className="text-gray-600 mt-1">{request.description}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {request.childInfo && request.childInfo.age} years, {request.childInfo && request.childInfo.gender}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {Array.isArray(request.workLocation) ? request.workLocation.join(', ') : request.workLocation}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {request.type}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">
                      {request.postedAt && request.postedAt.toDate ? request.postedAt.toDate().toLocaleDateString() : 'N/A'}
                    </div>
                    <div className="mt-1 text-sm font-medium">
                      {request.status === 'open' ? (
                        <span className="text-green-600">Open</span>
                      ) : (
                        <span className="text-gray-500">Closed</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    {request.responses ? request.responses.length : 0} responses
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleViewResponses(request.id)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      View Responses
                    </button>
                    {request.status === 'open' && (
                      <button
                        onClick={() => handleCloseRequest(request.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Close Request
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Helper to render request row with responses count ---
const RequestRow = React.memo(({ req }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!req.id) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    const q = query(
      collection(db, "expressedInterest"), 
      where("requestId", "==", req.id)
    );
    
    const unsub = onSnapshot(q, 
      (snap) => {
        setResponses(snap.docs.map(doc => doc.data()));
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching responses:", error);
        setLoading(false);
      }
    );
    
    return () => unsub();
  }, [req.id]);
  
  const lastResponse = responses.length > 0 
    ? new Date(Math.max(...responses.map(r => r.timestamp?.toMillis?.() || 0))).toLocaleDateString()
    : "No responses yet";
    
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm">{req.role || req.type}</td>
      <td className="px-4 py-3 text-sm">
        {req.postedAt?.toDate ? req.postedAt.toDate().toLocaleDateString() : 'N/A'}
      </td>
      <td className="px-4 py-3 text-sm">
        {req.location 
          ? (typeof req.location === 'string' 
              ? req.location 
              : req.location.city || Object.values(req.location).filter(Boolean).join(', ') || 'N/A')
          : 'N/A'}
        {req.workLocation?.[0] && ` (${req.workLocation[0]})`}
      </td>
      <td className="px-4 py-3 text-sm">
        <span className={`px-2 py-1 rounded-full text-xs ${
          req.status === 'open' ? 'bg-green-100 text-green-800' : 
          req.status === 'closed' ? 'bg-red-100 text-red-800' : 
          'bg-gray-100 text-gray-800'
        }`}>
          {req.status || 'Open'}
        </span>
      </td>
      <td className="px-4 py-3 text-sm">
        <div className="flex items-center space-x-2">
          <button 
            className="text-indigo-600 hover:text-indigo-900 hover:underline"
            onClick={() => console.log('View details', req.id)}
          >
            View Details
          </button>
          <span className="text-gray-300">|</span>
          <button 
            className="text-indigo-600 hover:text-indigo-900 hover:underline"
            onClick={() => console.log('View responses', responses)}
          >
            {loading ? '...' : responses.length} {responses.length === 1 ? 'Response' : 'Responses'}
          </button>
        </div>
      </td>
    </tr>
  );
});
